import { HsaCartWidgetPlacement } from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { useEffect, useRef, useState } from "react";
import { useLeakyDebounce } from "../../utils/use-leaky-debounce";
import { RedoHsaCartContainerElement } from "./cart-element";

export const HSA_CART_CONTAINER_ID = "redo-hsa-cart-container";

export interface HsaCartPortal {
  foundSelector: string;
  portal: RedoHsaCartContainerElement;
}

const positionMap: Record<InsertionPosition, InsertPosition> = {
  [InsertionPosition.BEFORE]: "beforebegin",
  [InsertionPosition.AFTER]: "afterend",
  [InsertionPosition.PREPEND]: "afterbegin",
  [InsertionPosition.APPEND]: "beforeend",
};

/**
 * Places the HSA/FSA cart widget host at every element matching the configured
 * placements, re-inserting on theme re-renders and pruning stale hosts.
 * Mirrors the delivery-estimate widget's multi-selector portal hook; the first
 * placement to match a given element wins.
 */
export function useHsaCartPortals({
  placements,
}: {
  placements: HsaCartWidgetPlacement[];
}): HsaCartPortal[] {
  const [tick, setTick] = useState(Symbol());
  const debouncedTick = useLeakyDebounce(tick, { outputHz: 4 });

  useEffect(() => {
    const observer = new MutationObserver(() => setTick(Symbol()));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const trackedRef = useRef(new Map<Element, RedoHsaCartContainerElement>());
  const [portals, setPortals] = useState<HsaCartPortal[]>([]);

  useEffect(() => {
    const tracked = trackedRef.current;

    if (placements.length === 0) {
      if (tracked.size > 0) {
        for (const element of tracked.values()) {
          element.remove();
        }
        tracked.clear();
        setPortals([]);
      }
      return;
    }

    RedoHsaCartContainerElement.register();

    for (const [target, element] of tracked) {
      if (!target.isConnected || !element.isConnected) {
        element.remove();
        tracked.delete(target);
      }
    }

    const next: HsaCartPortal[] = [];
    for (const placement of placements) {
      if (!placement.selector) {
        continue;
      }
      for (const target of document.querySelectorAll(placement.selector)) {
        let element = tracked.get(target);
        if (!element) {
          element = document.createElement(
            RedoHsaCartContainerElement.TAG,
          ) as RedoHsaCartContainerElement;
          element.id = HSA_CART_CONTAINER_ID;
          element.style.setProperty("display", "unset", "important");
          target.insertAdjacentElement(
            positionMap[placement.insertPosition],
            element,
          );
          tracked.set(target, element);
        }
        next.push({ foundSelector: placement.selector, portal: element });
      }
    }

    setPortals((prev) => (samePortals(prev, next) ? prev : next));
  }, [placements, debouncedTick]);

  useEffect(
    () => () => {
      for (const element of trackedRef.current.values()) {
        element.remove();
      }
      trackedRef.current.clear();
    },
    [],
  );

  return portals;
}

function samePortals(a: HsaCartPortal[], b: HsaCartPortal[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (const [i, element] of a.entries()) {
    const other = b[i]!;
    if (
      element.portal !== other.portal ||
      element.foundSelector !== other.foundSelector
    ) {
      return false;
    }
  }
  return true;
}
